import React, { useState, useEffect } from 'react'
import { Select, Typography } from '@telavita-core/react-design-kit'

import CheckBox from 'components/CheckBox'

import {
  MAX_ADULT_AGE,
  MAX_ELDERLY_AGE,
  MIN_ADULT_AGE,
  MIN_ELDERLY_AGE,
  AgeCategories,
} from 'settings/_meetsAgesSettings'

import * as S from './styles'

export interface Age {
  id: number
  label: string
  isSelected: boolean
  category: AgeCategories
}

interface AgeCategoryOption {
  id: number
  label: string
}

interface AgeCategory extends AgeCategoryOption {
  category: AgeCategories
  value: number
}

const agesCategoriesOptions: AgeCategory[] = [
  { id: 1, label: '3 anos', category: 'KID', value: 3 },
  { id: 2, label: '4 anos', category: 'KID', value: 4 },
  { id: 3, label: '5 anos', category: 'KID', value: 5 },
  { id: 4, label: '6 anos', category: 'KID', value: 6 },
  { id: 5, label: '7 anos', category: 'KID', value: 7 },
  { id: 6, label: '8 anos', category: 'KID', value: 8 },
  { id: 7, label: '9 anos', category: 'KID', value: 9 },
  { id: 8, label: '10 anos', category: 'KID', value: 10 },
  { id: 9, label: '11 anos', category: 'KID', value: 11 },
  { id: 10, label: '12 anos', category: 'KID', value: 12 },
  { id: 11, label: '13 anos', category: 'ADOLESCENT', value: 13 },
  { id: 12, label: '14 anos', category: 'ADOLESCENT', value: 14 },
  { id: 13, label: '15 anos', category: 'ADOLESCENT', value: 15 },
  { id: 14, label: '16 anos', category: 'ADOLESCENT', value: 16 },
  { id: 15, label: '17 anos', category: 'ADOLESCENT', value: 17 },
]

interface ProfessionalMeetsAgesFilterProps {
  ageRanges: string[]
  onChangeAgeRanges: (ageRanges: string[]) => void
  ages: Age[],
  onChangeAges: (ages: Age[]) => void
  ageRangeCategoryAdolescentError: boolean
  ageRangeCategoryKidError: boolean
  onChangeAgeRangeCategoryAdolescentError: (value: boolean) => void
  onChangeAgeRangeCategoryKidError: (value: boolean) => void
}

export function ProfessionalMeetsAgesFilter({
  ageRanges,
  onChangeAgeRanges,
  ages,
  onChangeAges,
  ageRangeCategoryAdolescentError,
  ageRangeCategoryKidError,
  onChangeAgeRangeCategoryAdolescentError,
  onChangeAgeRangeCategoryKidError,
}: ProfessionalMeetsAgesFilterProps): JSX.Element {
  const [selectedAgesCategories, setSelectedAgesCategories] = useState<AgeCategory[]>(() => {
    if (ageRanges.length > 0) {
      const ages = ageRanges.map(age => Number(age.substring(0, age.indexOf('-'))))
      
      const initialValues = ages.map(age => {
        const ageCategory = agesCategoriesOptions.find(option => option.value === age)

        return ageCategory
      })

      return initialValues.filter(Boolean)
    }

    return []
  })

  function handleSelectAge(ageId: number) {
    const selectedAgeIndex = ages.findIndex(age => age.id === ageId)
    const newAges = [...ages]

    newAges[selectedAgeIndex].isSelected = !newAges[selectedAgeIndex].isSelected
    onChangeAges(newAges)
  }

  function handleSelectAgeCategory(ageCategoryOption: AgeCategoryOption) {
    const selectedAgeCategory = agesCategoriesOptions.find(option => option.id === ageCategoryOption.id)

    const newAgesCategoriesOption = selectedAgesCategories.filter(option => option.category !== selectedAgeCategory.category)

    if (selectedAgeCategory.category === 'KID' && ageRangeCategoryKidError) onChangeAgeRangeCategoryKidError(false)
    if (selectedAgeCategory.category === 'ADOLESCENT' && ageRangeCategoryAdolescentError) onChangeAgeRangeCategoryAdolescentError(false)

    setSelectedAgesCategories([...newAgesCategoriesOption, selectedAgeCategory])
  }

  function formattedSelectedAgeCategory(ageCategory: AgeCategories) {
    const selectedAgeCategory = selectedAgesCategories.find(option => option.category === ageCategory)

    if (!selectedAgeCategory) return null

    const option = {
      id: selectedAgeCategory.id,
      label: selectedAgeCategory.label,
    } as AgeCategoryOption
    
    return option
  }

  function formattedAgesCategoriesOptions(ageCategory: AgeCategories) {
    const selectedAgesCategories = agesCategoriesOptions.filter(option => option.category === ageCategory)

    const options = selectedAgesCategories.map(ageCategory => {
      return {
        id: ageCategory.id,
        label: ageCategory.label,
      } as AgeCategoryOption
    })

    return options
  }

  useEffect(() => {
    function formatAgeRange(startAge: number, endAge: number) {
      return `${startAge}-${endAge}`
    }

    const selectedAges = ages.filter(age => age.isSelected)

    const newAgeRanges = selectedAges.map(age => {
      const kidAgeSelected = selectedAgesCategories.find(ageCategory => ageCategory.category === age.category && age.category === 'KID')?.value
      const adolescentAgeSelected = selectedAgesCategories.find(ageCategory => ageCategory.category === age.category && age.category === 'ADOLESCENT')?.value

      switch(age.category) {
      case 'ADULT':
        return formatAgeRange(MIN_ADULT_AGE, MAX_ADULT_AGE)
      case 'ELDERLY':
        return formatAgeRange(MIN_ELDERLY_AGE, MAX_ELDERLY_AGE)
      case 'ADOLESCENT':
        return adolescentAgeSelected && formatAgeRange(adolescentAgeSelected, adolescentAgeSelected)
      case 'KID':
        return kidAgeSelected && formatAgeRange(kidAgeSelected, kidAgeSelected)
      default:
        return null
      }
    })

    onChangeAgeRanges(newAgeRanges)

  }, [ages, selectedAgesCategories, onChangeAgeRanges])

  useEffect(() => {
    const filtersCleanHandled = ageRanges.length === 0
    const hasSelectedAges = ages.some(age => age.isSelected)

    const filtersCleanHandledAndHasSelectedAges = filtersCleanHandled && hasSelectedAges

    if (filtersCleanHandledAndHasSelectedAges) {
      const unselectAllAges = ages.map(age => {
        return {
          ...age,
          isSelected: false,
        }
      })

      onChangeAges(unselectAllAges)
      setSelectedAgesCategories([])
    }

  //eslint-disable-next-line
  }, [ageRanges])

  return (
    <S.MeetsAgesContainer>
      <Typography
        variant='content2'
        weight='bold'
      >
        Atendimento por idade
      </Typography>

      {ages.map(age => (
        <>
          <CheckBox
            key={age.id}
            title={age.label}
            isSelected={age.isSelected}
            onClickCheckBox={() => handleSelectAge(age.id)}
            customIconColor={age.isSelected ? '#1F35B5' : '#4B4B4B'}
            indeterminate={false}
          />

          {age.category === 'KID' && age.isSelected && (
            <div>
              <Select
                label=''
                placeholder='Selecione a idade da criança*'
                customClassName={`HomeFiltersAgeSelect ${ageRangeCategoryKidError && 'noSelectAgeError'}`}
                options={formattedAgesCategoriesOptions(age.category)}
                selected={formattedSelectedAgeCategory(age.category)}
                onSelect={handleSelectAgeCategory}
              />

              {ageRangeCategoryKidError && (
                <Typography
                  variant='content3'
                  color='danger'
                  inlineStyles={{ marginTop: 5 }}
                >
                  Selecione a idade
                </Typography>
              )}
            </div>

          )}

          {age.category === 'ADOLESCENT' && age.isSelected && (
            <div>
              <Select
                label=''
                placeholder='Selecione a idade do adolescente*'
                customClassName={`HomeFiltersAgeSelect ${ageRangeCategoryAdolescentError && 'noSelectAgeError'}`}
                options={formattedAgesCategoriesOptions(age.category)}
                selected={formattedSelectedAgeCategory(age.category)}
                onSelect={handleSelectAgeCategory}
              />

              {ageRangeCategoryAdolescentError && (
                <Typography
                  variant='content3'
                  color='danger'
                  inlineStyles={{ marginTop: 5 }}
                >
                  Selecione a idade
                </Typography>
              )}
            </div>
          )}
        </>
      ))}
    </S.MeetsAgesContainer>
  )
}